import React, { useState, useMemo, useRef, useEffect } from "react";
import { Search, X } from "lucide-react";

// Choix d'une entrée du référentiel KYC/LAB (Paramètres KYC/LAB) — code
// activité NAF, pays… Recherche instantanée par code ou libellé dans les
// listes déjà chargées (EtatGlobal.kycReferentiel). Seule une entrée
// existante peut être retenue : un texte saisi sans choisir de suggestion
// n'est jamais enregistré.
const NIVEAU_STYLE = {
  faible: "bg-emerald-50 text-emerald-700",
  moyen: "bg-amber-50 text-amber-700",
  eleve: "bg-rose-50 text-rose-700",
};
const NIVEAU_LIB = { faible: "Faible", moyen: "Moyen", eleve: "Élevé" };

const normaliser = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default function ReferentielSelect({
  entries,
  value,
  onChange,
  placeholder = "Rechercher par code ou libellé…",
  className = "",
  inputClassName = "",
  disabled,
  autoFocus,
}) {
  const [q, setQ] = useState("");
  const [ouvert, setOuvert] = useState(false);
  const [actif, setActif] = useState(0);
  const inputRef = useRef(null);
  const actuel = useMemo(() => (value ? (entries || []).find((e) => e.code === value) : null), [entries, value]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const resultats = useMemo(() => {
    const s = normaliser(q.trim());
    if (!s) return [];
    const parCode = [];
    const parLibelle = [];
    (entries || []).forEach((e) => {
      if (normaliser(e.code).startsWith(s)) parCode.push(e);
      else if (normaliser(`${e.code} ${e.libelle}`).includes(s)) parLibelle.push(e);
    });
    return [...parCode, ...parLibelle].slice(0, 15);
  }, [q, entries]);

  const choisir = (e) => {
    onChange(e);
    setQ("");
    setOuvert(false);
  };

  return (
    <div className={`relative ${className}`}>
      {actuel && !ouvert ? (
        <div className={`flex items-center gap-2 ${inputClassName}`}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setOuvert(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            className="flex-1 min-w-0 text-left truncate"
            title="Changer"
          >
            <b>{actuel.code}</b> — {actuel.libelle}
          </button>
          {actuel.niveau && (
            <span className={`text-[10px] px-1.5 rounded-full shrink-0 ${NIVEAU_STYLE[actuel.niveau]}`}>{NIVEAU_LIB[actuel.niveau]}</span>
          )}
          {!disabled && (
            <button type="button" onClick={() => onChange(null)} title="Retirer" className="text-slate-300 hover:text-rose-600 shrink-0">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            ref={inputRef}
            value={q}
            disabled={disabled}
            onChange={(e) => {
              setQ(e.target.value);
              setOuvert(true);
              setActif(0);
            }}
            onFocus={() => setOuvert(true)}
            onBlur={() => setTimeout(() => setOuvert(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActif((i) => Math.min(i + 1, resultats.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActif((i) => Math.max(i - 1, 0));
              } else if (e.key === "Enter" && resultats[actif]) {
                e.preventDefault();
                choisir(resultats[actif]);
              } else if (e.key === "Escape") {
                setQ("");
                setOuvert(false);
              }
            }}
            placeholder={placeholder}
            className={`w-full pl-6 ${inputClassName}`}
          />
        </div>
      )}
      {ouvert && q.trim() && (
        <div className="absolute z-30 mt-1 w-full min-w-[280px] max-h-64 overflow-y-auto bg-white rounded-lg shadow-xl border border-slate-100">
          {resultats.length === 0 ? (
            <p className="text-[11px] text-slate-400 px-3 py-2 m-0">Aucun résultat dans le référentiel KYC/LAB.</p>
          ) : (
            resultats.map((e, i) => (
              <button
                key={e.id}
                type="button"
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => choisir(e)}
                className={`w-full text-left px-2.5 py-1.5 flex items-center gap-2 text-xs ${i === actif ? "bg-slate-50" : "hover:bg-slate-50"}`}
              >
                <b className="w-14 shrink-0 text-slate-700">{e.code}</b>
                <span className="flex-1 text-slate-600">{e.libelle}</span>
                {e.niveau && (
                  <span className={`text-[10px] px-1.5 rounded-full shrink-0 ${NIVEAU_STYLE[e.niveau]}`}>{NIVEAU_LIB[e.niveau]}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
